import { ORG_PATH_PREFIX } from '@/lib/routes';
import { isConfigurePath } from '../ConfigureShell/configure-paths';

/**
 * Path classification for the non-Configure takeover surfaces. Same contract
 * as `configure-paths`: given a pathname, decide whether the app shell should
 * step aside — including alias and legacy-redirect shapes, so a redirect
 * hop never flashes the app sidebar or poisons the takeover return path.
 */

const INBOX_FIRST_SEGMENTS: ReadonlySet<string> = new Set(['inbox']);

const AUTOMATIONS_FIRST_SEGMENTS: ReadonlySet<string> = new Set(['automations', 'automation-runs']);

/**
 * Legacy `/flows*` shapes served by the flows plugin's alias redirect. They
 * immediately forward to the canonical `/automations*` URLs; classifying them
 * keeps the shell stable across the hop.
 */
const AUTOMATIONS_ALIAS_FIRST_SEGMENTS: ReadonlySet<string> = new Set(['flows', 'flow-runs']);

/**
 * True when the pathname is inside the Admin takeover surface (`/admin` or
 * `/admin/*`), EXCEPT for `/admin/agent-models` which is a legacy alias into
 * org Configure.
 */
export function isAdminPath(pathname: string): boolean {
  const segments = pathname.split('/').filter(Boolean);
  return segments[0] === 'admin' && segments[1] !== 'agent-models';
}

/**
 * True when the pathname is inside the Connect takeover surface
 * (`/orgs/:slug/configure/connect/...`). Carved out of {@link isConfigurePath}
 * so the Connect shell owns these paths.
 */
export function isConnectPath(pathname: string): boolean {
  const segments = pathname.split('/').filter(Boolean);
  if (segments[0] === ORG_PATH_PREFIX) {
    return segments[2] === 'configure' && segments[3] === 'connect';
  }
  return false;
}

/**
 * Root-relative plugin route paths (e.g. `/configure/connect/ai-gateway`)
 * that belong inside the Connect takeover shell. Mirrors
 * `isConfigurePluginRoutePath`.
 */
export function isConnectPluginRoutePath(path: string): boolean {
  const segments = path.split('/').filter(Boolean);
  return segments[0] === 'configure' && segments[1] === 'connect';
}

function orgFirstSegment(pathname: string): string | undefined {
  const segments = pathname.split('/').filter(Boolean);
  if (segments[0] === ORG_PATH_PREFIX) return segments[2];
  // Legacy slug-less paths (`/inbox`, `/automations`, `/flows`) hop through
  // LegacyRedirect to the org-scoped URL; classify them by their first segment.
  return segments[0];
}

export function isInboxPath(pathname: string): boolean {
  const first = orgFirstSegment(pathname);
  return first !== undefined && INBOX_FIRST_SEGMENTS.has(first);
}

export function isAutomationsPath(pathname: string): boolean {
  const first = orgFirstSegment(pathname);
  return (
    first !== undefined &&
    (AUTOMATIONS_FIRST_SEGMENTS.has(first) || AUTOMATIONS_ALIAS_FIRST_SEGMENTS.has(first))
  );
}

/**
 * Root-relative plugin route paths (e.g. `/automations/:flowId/edit`) that
 * belong inside the Automations takeover shell. Mirrors
 * `isConfigurePluginRoutePath`.
 */
export function isAutomationsPluginRoutePath(path: string): boolean {
  const first = path.split('/').filter(Boolean)[0] ?? '';
  return AUTOMATIONS_FIRST_SEGMENTS.has(first) || AUTOMATIONS_ALIAS_FIRST_SEGMENTS.has(first);
}

/**
 * Core Labs segments known statically: the chat surface and the misc /
 * plugins overview subtrees. Plugin-contributed labs pages (top-level
 * paths registered under the Misc and Plugins nav groups, e.g. `/town`)
 * are classified dynamically via {@link labsExtraFirstSegments}.
 */
/**
 * Replays (agent and automation alike) are labs content — saved runs you
 * play back — so the automation-replay pages and their legacy `/flow-replays`
 * alias classify here, not under Automations.
 */
const LABS_CORE_FIRST_SEGMENTS: ReadonlySet<string> = new Set([
  'chat',
  'misc',
  'plugins',
  'automation-replays',
  'flow-replays',
]);

/**
 * First segments of plugin-contributed labs pages, derived from nav items
 * in the `misc` and `plugins` groups. Pass unscoped plugin nav items (their
 * `to` is root-relative, e.g. `/town`).
 */
export function labsExtraFirstSegments(
  navItems: ReadonlyArray<{ to: string; group?: string; rootLevel?: boolean }>,
): ReadonlySet<string> {
  const segments = new Set<string>();
  for (const item of navItems) {
    if (item.group !== 'misc' && item.group !== 'plugins') continue;
    // Root-level items (e.g. /docs) and settings-shaped targets
    // (/org/plugins/<name> config pages) are not labs pages; deriving
    // their first segments would claim /docs or the whole /org subtree.
    if (item.rootLevel) continue;
    const first = item.to.split('/').filter(Boolean)[0];
    if (!first || first === 'org') continue;
    segments.add(first);
  }
  return segments;
}

/** Labs pages where `esc` exits the takeover; plugin pages keep `esc` local. */
export function isLabsCorePath(pathname: string): boolean {
  const first = orgFirstSegment(pathname);
  return first !== undefined && LABS_CORE_FIRST_SEGMENTS.has(first);
}

export function isLabsPath(pathname: string, extraFirstSegments?: ReadonlySet<string>): boolean {
  const first = orgFirstSegment(pathname);
  if (first === undefined) return false;
  return LABS_CORE_FIRST_SEGMENTS.has(first) || (extraFirstSegments?.has(first) ?? false);
}

/**
 * Built-in account routes that always anchor the account takeover: personal
 * `/profile` (and `/profile/:userId`) and `/settings`. Root-level only — the
 * org-scoped `/orgs/:slug/configure/organization/general` settings lives in
 * the Configure takeover, not here.
 */
const ACCOUNT_BUILTIN_FIRST_SEGMENTS: ReadonlySet<string> = new Set(['profile', 'settings']);

/**
 * First segments of plugin-contributed account pages, derived from nav items
 * in the `user-menu` group that resolve to an in-app route (not a hash
 * command, not `openInNewWindow`). Pass unscoped plugin nav items. Mirrors
 * {@link labsExtraFirstSegments}: the account takeover classifies these
 * dynamically so a plugin that adds an account page (`/tutorials`,
 * `/notifications`) gets the takeover chrome without a code change here.
 */
export function accountExtraFirstSegments(
  navItems: ReadonlyArray<{ to: string; group?: string; openInNewWindow?: boolean }>,
): ReadonlySet<string> {
  const segments = new Set<string>();
  for (const item of navItems) {
    if (item.group !== 'user-menu') continue;
    if (item.openInNewWindow) continue;
    if (item.to.startsWith('#')) continue;
    const first = item.to.split('/').filter(Boolean)[0];
    if (!first || first === ORG_PATH_PREFIX) continue;
    segments.add(first);
  }
  return segments;
}

/**
 * True when `pathname` is an account-section page: a built-in account route
 * (`/profile`, `/profile/:userId`, `/settings`) or a plugin-contributed
 * user-menu route (e.g. `/tutorials`, `/notifications`). Built-in segments
 * only match root-level paths (an org-scoped `/orgs/:slug/settings` is the
 * Configure surface, not account); plugin segments match both root-level and
 * org-scoped shapes (e.g. `/tutorials` and `/orgs/:slug/notifications`).
 */
export function isAccountPath(pathname: string, extraFirstSegments?: ReadonlySet<string>): boolean {
  const segments = pathname.split('/').filter(Boolean);
  const isOrgScoped = segments[0] === ORG_PATH_PREFIX;
  const first = isOrgScoped ? segments[2] : segments[0];
  if (first === undefined) return false;
  if (extraFirstSegments?.has(first)) return true;
  // Built-in account segments are root-level only, so an org-scoped path
  // whose tail happens to be `settings` (e.g. /orgs/:slug/...) is not claimed.
  return !isOrgScoped && ACCOUNT_BUILTIN_FIRST_SEGMENTS.has(first);
}

/**
 * Root-relative plugin route paths that belong inside the Account takeover
 * shell (e.g. `/tutorials`, `/notifications`). Mirrors
 * {@link isLabsPluginRoutePath}; `extraFirstSegments` carries the
 * manifest-derived user-menu segments. Built-in account routes (`/profile`,
 * `/settings`) are host routes mounted directly under the shell, not plugin
 * routes, so they are excluded here.
 */
export function isAccountPluginRoutePath(
  path: string,
  extraFirstSegments: ReadonlySet<string>,
): boolean {
  const first = path.split('/').filter(Boolean)[0] ?? '';
  return extraFirstSegments.has(first);
}

/**
 * Root-relative plugin route paths that belong inside the Labs takeover
 * shell. Mirrors `isConfigurePluginRoutePath`; `extraFirstSegments` carries
 * the manifest-derived plugin segments.
 */
export function isLabsPluginRoutePath(
  path: string,
  extraFirstSegments: ReadonlySet<string>,
): boolean {
  const first = path.split('/').filter(Boolean)[0] ?? '';
  return LABS_CORE_FIRST_SEGMENTS.has(first) || extraFirstSegments.has(first);
}

/**
 * True when the app shell should step aside for a dedicated takeover
 * surface: Configure, inbox, automations, or the statically-known part of
 * Labs. Plugin-contributed labs pages need the dynamic check
 * (`isLabsPath` with {@link labsExtraFirstSegments}) on top.
 */
export function isTakeoverPath(pathname: string): boolean {
  return (
    isConfigurePath(pathname) ||
    isConnectPath(pathname) ||
    isInboxPath(pathname) ||
    isAutomationsPath(pathname) ||
    isLabsCorePath(pathname) ||
    isAdminPath(pathname)
  );
}

/**
 * Whether a visited path may be remembered as the takeover return target.
 * Rejects takeover paths themselves, and pre-`/orgs` slug-ful shapes whose
 * tail is takeover-shaped (`/acme/automations` or legacy `/acme/flows`): those
 * hop through `LegacyOrgPathRedirect` into a takeover, and the redirect's
 * effect can run before the app shell's tracker — remembering the
 * pre-redirect URL would turn Back / `esc` into a loop straight back into
 * the takeover.
 */
export function shouldRememberReturnPath(
  pathname: string,
  labsExtra?: ReadonlySet<string>,
  accountExtra?: ReadonlySet<string>,
): boolean {
  const inTakeover = (p: string) =>
    isTakeoverPath(p) || isLabsPath(p, labsExtra) || isAccountPath(p, accountExtra);
  if (inTakeover(pathname)) return false;

  // The tutorial launcher (`/tutorial/:name`) is a transient route that
  // immediately replaces itself with the tutorial's target page. If the
  // replacement hasn't landed yet when the tracker fires, remembering the
  // launcher URL would turn the Back button into a loop that restarts the
  // tutorial.
  const segments = pathname.split('/').filter(Boolean);
  if (segments[0] === 'tutorial') return false;

  if (segments.length >= 2 && segments[0] !== ORG_PATH_PREFIX) {
    return !inTakeover('/' + segments.slice(1).join('/'));
  }
  return true;
}
